// redux/slices/surveySetsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
    url,
    method = "GET",
    data = null,
    headers = {},
}) => {
    const jwt = await getJwtToken();

    try {
        const response = await axios({
            url: `${getServerUrl()}${url}`,
            method,
            headers: {
                "Content-Type": "application/json",
                ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
                ...headers,
            },
            data,
            timeout: 15000,
        });

        return response.data;
    } catch (error) {
        throw {
            status: error?.response?.status,
            data: error?.response?.data,
            message:
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Request failed",
        };
    }
};

const getErrorMessage = (error, fallback) =>
    error?.data?.error ||
    error?.data?.message ||
    error?.message ||
    fallback;

/* ===================== THUNKS ===================== */

export const fetchSurveySets = createAsyncThunk(
    "surveySets/fetch",
    async (uid, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error("UID не передан");

            const data = await axiosRequest({
                url: `/surveySets/${uid}`,
            });

            return Array.isArray(data) ? data : [];
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Fetch Survey Sets failed")
            );
        }
    }
);

export const createSurveySet = createAsyncThunk(
    "surveySets/create",
    async (payload, { rejectWithValue }) => {
        try {
            const data = await axiosRequest({
                url: "/surveySets",
                method: "POST",
                data: payload,
            });

            return data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Create Survey Set failed")
            );
        }
    }
);

export const updateSurveySet = createAsyncThunk(
    "surveySets/update",
    async ({ id, ...payload }, { rejectWithValue }) => {
        try {
            if (!id) throw new Error("ID не передан");

            const data = await axiosRequest({
                url: `/surveySets/${id}`,
                method: "PUT",
                data: payload,
            });

            return data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Update Survey Set failed")
            );
        }
    }
);

export const deleteSurveySet = createAsyncThunk(
    "surveySets/delete",
    async (id, { rejectWithValue }) => {
        try {
            if (!id) throw new Error("ID не передан");

            await axiosRequest({
                url: `/surveySets/${id}`,
                method: "DELETE",
            });

            return id;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Delete Survey Set failed")
            );
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    sets: [],
    status: "idle",
    error: null,
};

/* ===================== SLICE ===================== */

const surveySetsSlice = createSlice({
    name: "surveySets",
    initialState,
    reducers: {
        clearSurveySetsError: (state) => {
            state.error = null;
        },
        resetSurveySetsStatus: (state) => {
            state.status = "idle";
            state.error = null;
        },
        clearSurveySets: (state) => {
            state.sets = [];
            state.status = "idle";
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSurveySets.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchSurveySets.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.sets = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchSurveySets.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Fetch Survey Sets failed";
            })

            .addCase(createSurveySet.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(createSurveySet.fulfilled, (state, action) => {
                state.status = "succeeded";

                if (action.payload) {
                    state.sets.push(action.payload);
                }
            })
            .addCase(createSurveySet.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Create Survey Set failed";
            })

            .addCase(updateSurveySet.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(updateSurveySet.fulfilled, (state, action) => {
                state.status = "succeeded";

                const updated = action.payload;
                if (!updated?.id) return;

                const index = state.sets.findIndex(
                    (item) => String(item?.id) === String(updated.id)
                );

                if (index !== -1) {
                    state.sets[index] = updated;
                }
            })
            .addCase(updateSurveySet.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Update Survey Set failed";
            })

            .addCase(deleteSurveySet.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(deleteSurveySet.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.sets = state.sets.filter(
                    (item) => String(item?.id) !== String(action.payload)
                );
            })
            .addCase(deleteSurveySet.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Delete Survey Set failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const {
    clearSurveySetsError,
    resetSurveySetsStatus,
    clearSurveySets,
} = surveySetsSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectSurveySetsState = (state) => state.surveySets;
export const selectSurveySets = (state) => state.surveySets?.sets || [];
export const selectSurveySetsStatus = (state) =>
    state.surveySets?.status || "idle";
export const selectSurveySetsError = (state) =>
    state.surveySets?.error || null;

export default surveySetsSlice.reducer;